import type { PostMessageSubmission, PostMessageSubProvider } from './types';

/**
 * Cal.com embed detection (bookings + routing forms).
 *
 * Cal.com iframes send postMessages to the parent window with this shape:
 *   { originator: "CAL", type: "bookingSuccessfulV2", namespace: "talk",
 *     fullType: "CAL:talk:bookingSuccessfulV2", data: { ... } }
 *
 * The namespace is dynamic (e.g., "talk", "sales") but event types are fixed.
 *
 * Submission events we treat as conversions:
 *   - `bookingSuccessful` / `bookingSuccessfulV2` — user booked a slot.
 *   - `routed` — Cal.com Routing Form submission (data.actionType ∈
 *     {customPageMessage, externalRedirectUrl, eventTypeRedirectUrl}). For
 *     `eventTypeRedirectUrl` the booking page loads inside the embed and
 *     `bookingSuccessful*` fires later if the user completes the booking; we
 *     still fire on `routed` so a filled routing form (qualified lead) is
 *     captured even when the user abandons before booking.
 *
 * No custom setup needed — the parent PostMessageStrategy's message listener
 * handles everything via detect/isSubmission/extractData.
 */
export const CalComProvider: PostMessageSubProvider = {
    name: 'calcom',
    trustedOrigins: /^https:\/\/([\w-]+\.)?cal\.com$/,

    detect(data: Record<string, unknown>): boolean {
        return data?.originator === 'CAL';
    },

    isSubmission(data: Record<string, unknown>): boolean {
        const type = (data?.type as string) || '';
        if (type.startsWith('bookingSuccessful')) return true;
        if (type === 'routed') return true;
        return false;
    },

    extractData(data: Record<string, unknown>, origin: string): PostMessageSubmission {
        const calData = (data?.data || {}) as Record<string, unknown>;
        const type = (data?.type as string) || '';
        const fullType = (data?.fullType as string) || '';

        if (type === 'routed') {
            const actionType = (calData?.actionType as string) || '';
            const actionValue = (calData?.actionValue as string) || '';
            const responseId = calData?.responseId as string | number | undefined;
            const indicators = [
                'postmessage_calcom_routingform_submitted',
                `calcom_event_type:${fullType}`,
            ];
            if (actionType) indicators.push(`calcom_routed_action:${actionType}`);
            if (responseId !== undefined && responseId !== null) {
                indicators.push(`calcom_routingform_response_id:${responseId}`);
            }
            if (actionValue) indicators.push(`calcom_routed_value:${actionValue}`);
            return {
                provider: 'calcom',
                formId:
                    responseId !== undefined && responseId !== null
                        ? `calcom_routingform_${responseId}`
                        : 'calcom_routingform',
                applyFormIdWhitelist: responseId !== undefined && responseId !== null,
                indicators,
                origin,
            };
        }

        const indicators = [
            'postmessage_calcom_booking_completed',
            `calcom_event_type:${fullType}`,
        ];
        if (calData?.uid) {
            indicators.push(`calcom_booking_uid:${calData.uid}`);
        }
        return {
            provider: 'calcom',
            formId: calData?.eventTypeId ? `calcom_event_${calData.eventTypeId}` : `calcom_${type}`,
            applyFormIdWhitelist: Boolean(calData?.eventTypeId),
            indicators,
            origin,
        };
    },
};
